import React from 'react';
import { useDispatch } from 'react-redux';
import HfReactHelper from 'common/utils/hf-react-helper';
import {
  OneDriveIntegration, UploadIntegrationType,
} from 'hellospa/page/prep-and-send/data/types/integration';
import ReAuthModal from './reauth-modal';
import oneDriveIcon from 'images/external_logo_med_T.png';
import { useLatestRef, useForceUpdate } from 'hellospa/common/hooks';

type FileValue = {
  '@microsoft.graph.downloadUrl': string,
  name: string,
}

type OneDriveFile = {
  value: FileValue[],
}

type PickerOptionsAdvanced = {
  redirectUri: string,
  accessToken?: string,
  queryParameters: string,
}

type PickerOptions = {
  clientId: string,
  action: string,
  multiSelect: boolean,
  advanced: PickerOptionsAdvanced,
  success: (files: OneDriveFile) => void,
  cancel: () => void,
  error: (err: any) => void,
}

type OneDrive = {
  open: (options: PickerOptions) => void,
}

declare global {
  interface Window { OneDrive: OneDrive; }
}

type Props = {
  settings: OneDriveIntegration,
  shouldTriggerPicker: boolean,
  onSuccess: (id: string, name: string) => void
  toggleIntegrationPicker: (integration: string, status: boolean) => void,
}

const SCRIPT_SRC = 'https://js.live.net/v7.2/OneDrive.js';
const REDIRECT_URI = window.location.protocol + HfReactHelper.urlHelper('home/oneDrivePicker');
const QUERY_PARAMETERS = 'select=id,name,size,file,folder,photo,@microsoft.graph.downloadUrl';
const SCRIPT_ID = 'onedrivejs';

/**
 * Load the script to inject into the DOM (happens on component mount)
 * @param  {OneDriveIntegration} settings
 * @return {void}
 */
function initialize(settings: OneDriveIntegration) {
  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.src = SCRIPT_SRC;
  script.setAttribute('nonce', settings.nonce);
  script.async = true;

  return script;
}

export default function OneDriveFilePicker({
  settings,
  shouldTriggerPicker,
  onSuccess,
  toggleIntegrationPicker,
}: Props) {
  const dispatch = useDispatch();

  React.useEffect(() => {
    const script = initialize(settings);
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [settings]);


  // NOTE: The useEffect needs to be stable, meaning it only runs the effect
  // when `settings.enabled` or `shouldTriggerPicker` change. onSuccess is not
  // stable on its own unless the parent wraps it in `useCallback`. By using a
  // ref here the callback can continue to be updated without triggering
  // useEffect. If you want to see the bug this fixes, add `onSuccess` to the
  // dependency array.  If this re-renders while the picker is open, it'll
  // flicker beause it'll open a new picker.
  const successRef = useLatestRef(onSuccess);
  const forceUpdate = useForceUpdate();
  const OneDrive = window.OneDrive;

  React.useEffect(() => {
    // ig no window.OnceDrive is set we forceUpdate
    if (!OneDrive) {
      setInterval(() => forceUpdate(), 500);
    }
  }, [OneDrive, forceUpdate]);

  React.useEffect(() => {
    // trigger picker here, also check if it's integrated already
    if (shouldTriggerPicker && settings.enabled && OneDrive != null) {
      const options: PickerOptions = {
        clientId: settings.clientId,
        action: 'download',
        multiSelect: false,
        advanced: {
          redirectUri: REDIRECT_URI,
          accessToken: settings.token,
          queryParameters: QUERY_PARAMETERS,
        },

        success: (file: OneDriveFile) => {
          // select the first value since multi select is disabled currently
          const value = file.value.length > 0 ? file.value[0] : null;
          if (value) {
            successRef.current(value['@microsoft.graph.downloadUrl'], value.name);
          }
          // set the integration value in state to false in file-picker after retrieval or not
          toggleIntegrationPicker(UploadIntegrationType.OneDrive, false);
        },

        // clicking the close button on the file picker window
        cancel: () => {
          toggleIntegrationPicker(UploadIntegrationType.OneDrive, false);
        },

        error: (_error: any) => {
          // not displaying because error comes from OneDrive & is not localized?
          // eslint-disable-next-line no-console
          console.log('OneDrive Error!');
          toggleIntegrationPicker(UploadIntegrationType.OneDrive, false);
        },
      };

      // initialize the OneDrive file picker
      OneDrive.open(options);
    }
  }, [dispatch, settings, shouldTriggerPicker, successRef, toggleIntegrationPicker, OneDrive]);

  // if clicked on the integration icon but it's not enabled,
  // trigger the modal to initiate auth
  if (shouldTriggerPicker && !settings.enabled) {
    return (
      <ReAuthModal
        integration={UploadIntegrationType.OneDrive}
        integrationIcon={oneDriveIcon}
        isOpen={shouldTriggerPicker && !settings.enabled}
        onClose={() => toggleIntegrationPicker(UploadIntegrationType.OneDrive, false)} />
    );
  }

  return null;
}
